import type { ErrorCopy } from '@shared/errors/catalog';

/**
 * The `SUPPORT_*` codes only the case detail meets.
 *
 * **Never inline a backend code in JSX.** The code is a log key; the sentence is
 * what reaches the operator; the `X-Request-ID` stays in the log line.
 *
 * `SUPPORT_CASE_CLOSED` is the sharpest of them, and the console should rarely
 * reach it: the composer is not drawn on a closed case, because the catalog
 * settles that `resolved` accepts a reply and `closed` does not. An operator who
 * meets this code had the case closed underneath them between load and send, so
 * the copy names the way forward — reopen it from the control on the same page —
 * rather than restating the refusal.
 *
 * `SUPPORT_CASE_STATE_UNKNOWN` carries the live vocabulary in `data.allowed`,
 * which means the console's own option list is stale: the copy sends the
 * operator to a reload rather than to a support ticket.
 *
 * Four codes arrived with the decision when it moved onto this surface.
 * `SUPPORT_BLOCK_REQUIRES_ADMIN` is the server half of a rule the console also
 * renders — the rider is drawn from the permission set, and an operator who meets
 * this code has had their role change underneath them.
 * `SUPPORT_BLOCK_ON_DISMISS` is a business invariant rather than a schema
 * refusal: dismissing resolves against nobody, so a block riding on it has no
 * subject; the console hides the box in that mode and the service refuses it
 * anyway — one rule, two enforcement points. `SUPPORT_UPSTREAM_TIMEOUT` is the
 * sharpest of them on a resolution: the call reached `backend-content`, where a
 * delete is irreversible, so the copy states the outcome is **unknown** and
 * directs to a reload rather than offering a retry the operator would press twice.
 */
export const errors: Readonly<Record<string, ErrorCopy>> = {
  SUPPORT_SCOPE_REQUIRED: {
    message: 'العمل على الحالات يحتاج دور مشغّل في الدعم، ولا يحمله حسابك.',
    tone: 'warning',
  },
  SUPPORT_CASE_NOT_FOUND: {
    message: 'لم نعثر على هذه الحالة. قد تكون حُذفت بينما كانت الصفحة مفتوحة.',
    tone: 'warning',
  },
  SUPPORT_CASE_CLOSED: {
    message: 'أُغلقت هذه الحالة قبل إرسال ردّك. أعد وضعها إلى «مفتوحة» من الضابط أدناه ثمّ أعد الإرسال.',
    tone: 'warning',
  },
  SUPPORT_CASE_STATE_UNKNOWN: {
    message: 'هذا الوضع لم يعد ضمن الأوضاع المهيّأة. أعد تحميل الصفحة لقراءة القائمة الحالية.',
    tone: 'warning',
  },
  SUPPORT_BAD_INPUT: {
    message: 'راجع الحقول المعلَّمة ثمّ أعد الإرسال.',
    tone: 'warning',
  },
  SUPPORT_BLOCK_REQUIRES_ADMIN: {
    message: 'حجب المالك من صلاحية المدير. احسم البلاغ دون الحجب، أو راجع مسؤولاً ليتولّاه.',
    tone: 'warning',
  },
  SUPPORT_BLOCK_ON_DISMISS: {
    message: 'لا يُحجب المالك مع صرف النظر — الحجب يرافق قراراً يقع ضدّه.',
    tone: 'warning',
  },
  SUPPORT_RESOLUTION_NOT_APPLICABLE: {
    message: 'هذه القرارات تخصّ بلاغات المحتوى وحدها. اعمل على هذه الحالة من الخيط والضابط أعلاه.',
    tone: 'warning',
  },
  SUPPORT_CONFLICT: {
    message: 'حُسم هذا البلاغ بالفعل. أعد تحميل الصفحة لقراءة قراره.',
    tone: 'warning',
  },
  SUPPORT_RATE_LIMITED: {
    message: 'رسائل كثيرة في وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  SUPPORT_UPSTREAM_FAILED: {
    message: 'الخدمة غير متاحة الآن. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  SUPPORT_UPSTREAM_TIMEOUT: {
    message:
      'تأخّرت الخدمة عن الردّ ونتيجة القرار غير معروفة. أعد تحميل الصفحة للاطّلاع عليها قبل أيّ محاولة أخرى.',
    tone: 'neutral',
  },
};
